import { ensureDriverAssigned } from 'Util/_HumanWritten/DriverAssignment';
import {
	BookingWizardData,
} from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';
import { Alignment, ButtonGroup } from 'Views/Components/Button/ButtonGroup';
import { Button } from 'Views/Components/Button/Button';
import React from 'react';

import { useIntl } from 'react-intl';
import { appConfigStore } from 'Models/AppConfigStore';
import classNames from 'classnames';
import If from 'Views/Components/If/If';
import { UpdateWizardData } from './FerryFilterPassengers';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import {
	getDefaultVehicleWizardData,
	VehicleBookingWizardData,
} from '../../../BookingWizardData/VehicleBookingWizardData';
import { useBookingWizardDataStore } from '../../../BookingWizardDataStore';
import {
	getDefaultPassengerWizardData,
	PassengerBookingWizardData,
} from '../../../BookingWizardData/PassengerBookingWizardData';
import {
	FreightBookingWizardData,
	getDefaultFreightWizardData,
} from '../../../BookingWizardData/FreightBookingWizardData';
import { TabSelected } from '../../../BookingWizardTypes';

export interface BookingTypeButtonsProps {
	editMode: boolean;
}

export function BookingTypeButtons({ editMode }: BookingTypeButtonsProps) {
	const intl = useIntl();
	const { wizardData, setWizardData } = useBookingWizardDataStore(x => ({
		wizardData: x.wizardData,
		setWizardData: x.setWizardData,
	}));

	const onVehicleTab = wizardData.tabSelected === 'vehicle';
	const onPassengerTab = wizardData.tabSelected === 'passenger';
	const onFreightTab = wizardData.tabSelected === 'freight';

	const bookingTypeSingleLineStyling = appConfigStore.oddNumberOfBookingTypes;

	return (
		<ButtonGroup
			className={classNames('booking-type__btn-group', {
				'booking-type-single-line': bookingTypeSingleLineStyling,
			})}
			alignment={Alignment.HORIZONTAL}
		>
			<If condition={appConfigStore.vehicleEnabled}>
				<Button
					className={onVehicleTab ? 'selected-btn' : 'unselected-btn'}
					disabled={
						editMode || (wizardData.bookingToEdit !== '' && wizardData.innerWizardData.type === 'freight')
					}
					onClick={() => onTabSelected(wizardData, setWizardData, 'vehicle')}
					buttonProps={{ 'data-test-id': 'wizard-booking-type-vehicle' } as any}
				>
					{intl
						.formatMessage({
							id: 'vehicle__name__label',
							defaultMessage: 'Vehicle',
						})
						.toUpperCase()}
				</Button>
			</If>
			<If condition={appConfigStore.showPassengerBookingOption}>
				<Button
					className={onPassengerTab ? 'selected-btn' : 'unselected-btn'}
					disabled={
						editMode || (wizardData.bookingToEdit !== '' && wizardData.innerWizardData.type === 'freight')
					}
					onClick={() => onTabSelected(wizardData, setWizardData, 'passenger')}
					buttonProps={{ 'data-test-id': 'wizard-booking-type-passenger' } as any}
				>
					{intl
						.formatMessage({
							id: 'passenger__name__label',
							defaultMessage: 'Walk-on',
						})
						.toUpperCase()}
				</Button>
			</If>
			<If condition={appConfigStore.freightEnabled && !wizardData.isBulkBookingFlow}>
				<Button
					className={onFreightTab ? 'selected-btn' : 'unselected-btn'}
					disabled={
						editMode || (wizardData.bookingToEdit !== '' && wizardData.innerWizardData.type !== 'freight')
					}
					onClick={() => onTabSelected(wizardData, setWizardData, 'freight')}
					buttonProps={{ 'data-test-id': 'wizard-booking-type-freight' } as any}
				>
					{intl
						.formatMessage({
							id: 'freight__name__label',
							defaultMessage: 'Freight',
						})
						.toUpperCase()}
				</Button>
			</If>
		</ButtonGroup>
	);
}

function onTabSelected(wizardData: BookingWizardData, onUpdateFilters: UpdateWizardData, tab: TabSelected) {
	const newData = new BookingWizardData({ ...wizardData });

	if (tab === 'passenger' && !(newData.innerWizardData instanceof PassengerBookingWizardData)) {
		switchToPassengerWizardData(newData, wizardData);
	}
	if (tab === 'vehicle' && !(newData.innerWizardData instanceof VehicleBookingWizardData)) {
		switchToVehicleWizardData(newData, wizardData);
	}
	if (tab === 'freight' && !(newData.innerWizardData instanceof FreightBookingWizardData)) {
		switchToFreightWizardData(newData);
	}

	onUpdateFilters(newData);
}

export function switchToPassengerWizardData(newData: BookingWizardData, oldData: BookingWizardData) {
	newData.tabSelected = 'passenger';
	newData.automaticallyApplyAddOnsToBothDirections = true;

	newData.innerWizardData = getDefaultPassengerWizardData();
	if (oldData.innerWizardData instanceof VehicleBookingWizardData) {
		newData.innerWizardData.adultTickets = oldData.innerWizardData.adultTickets;
		newData.innerWizardData.childTickets = oldData.innerWizardData.childTickets;
		newData.innerWizardData.infantTickets = oldData.innerWizardData.infantTickets;
		newData.innerWizardData.passengerDTickets = oldData.innerWizardData.passengerDTickets;
		newData.innerWizardData.passengerETickets = oldData.innerWizardData.passengerETickets;
		newData.innerWizardData.passengerFTickets = oldData.innerWizardData.passengerFTickets;
		newData.innerWizardData.passengerGTickets = oldData.innerWizardData.passengerGTickets;
		newData.innerWizardData.passengerHTickets = oldData.innerWizardData.passengerHTickets;

		// The driver flag is deliberately left alone here.
		//
		// This used to clear it, which meant walk-on → vehicle could not put the role back on the
		// person who had it: the way back ran `ensureDriverAssigned`, which hands it to the first
		// eligible passenger type. On a booking whose driver was a senior or a student, a round trip
		// through this button silently made them an adult — and the driver's passenger type is a
		// pricing input, so the fare moved with it.
		//
		// A walk-on booking must still not be *saved* carrying a driver. That is enforced where it
		// belongs, at submit (`dataToFerryTripBookingDto`), with `ApplyDriverFlag` serverside as a
		// backstop. Carrying the flag in memory on a booking type that does not use it costs
		// nothing; destroying it loses information we cannot get back.
	}

	if (!oldData.userSelectedTripType) {
		newData.tripType = whiteLabelStore.GetPassengerDefaultTripType;
		if (newData.tripType === 'one way') {
			newData.returningTicketId = '';
		}
	}
}

export function switchToVehicleWizardData(newData: BookingWizardData, oldData: BookingWizardData) {
	newData.tabSelected = 'vehicle';
	newData.automaticallyApplyAddOnsToBothDirections = true;

	newData.innerWizardData = getDefaultVehicleWizardData();
	if (oldData.innerWizardData instanceof PassengerBookingWizardData) {
		newData.innerWizardData.adultTickets = oldData.innerWizardData.adultTickets;
		newData.innerWizardData.childTickets = oldData.innerWizardData.childTickets;
		newData.innerWizardData.infantTickets = oldData.innerWizardData.infantTickets;
		newData.innerWizardData.passengerDTickets = oldData.innerWizardData.passengerDTickets;
		newData.innerWizardData.passengerETickets = oldData.innerWizardData.passengerETickets;
		newData.innerWizardData.passengerFTickets = oldData.innerWizardData.passengerFTickets;
		newData.innerWizardData.passengerGTickets = oldData.innerWizardData.passengerGTickets;
		newData.innerWizardData.passengerHTickets = oldData.innerWizardData.passengerHTickets;

		// Only if nobody is already carrying it. Since the walk-on switch above no longer clears the
		// flag, a booking that came from the vehicle tab arrives back here with its original driver
		// intact and `ensureDriverAssigned` leaves them alone — which is the whole point. It still
		// assigns one for a booking that started as walk-on and has never had a driver.
		ensureDriverAssigned(newData.innerWizardData);
	} else {
		// Ensure that there are no passengers
		newData.innerWizardData.adultTickets = [];
	}
	if (!oldData.userSelectedTripType) {
		newData.tripType = whiteLabelStore.GetVehicleDefaultTripType;
		if (newData.tripType === 'one way') {
			newData.returningTicketId = '';
		}
	}
}

export function switchToFreightWizardData(newData: BookingWizardData) {
	newData.tabSelected = 'freight';
	newData.automaticallyApplyAddOnsToBothDirections = true;

	newData.innerWizardData = getDefaultFreightWizardData();
	newData.tripType = 'one way';
	newData.returningTicketId = '';
}
